import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dialog heading — "Name this trip" when saving, "Rename this trip" when editing. */
  title: string;
  /** Prefilled value. For a save this is usually "Start → Destination". */
  defaultValue?: string;
  confirmLabel?: string;
  /** Called with the trimmed name. Never called with an empty string. */
  onConfirm: (name: string) => void;
}

/**
 * Ask for a trip name.
 *
 * REPLACES `window.prompt`, WHICH FROZE THE APP (Aug 30). `window.prompt` in a
 * Capacitor WebView is a NATIVE modal: it blocks the web view's main thread
 * for as long as it is up, and WebKit defers every touch behind it. The device
 * log showed gesture handling blocked for 22.4 s around a save, followed by
 * `Terminated due to signal 9` -- the content process killed, which the user
 * sees as the app freezing and then going white. Both places that asked for a
 * trip name used it, and both were on the save path Morgan kept freezing on.
 *
 * The same applies to `alert` and `confirm`. Nothing in the app should call
 * them; use a dialog.
 *
 * Max length matches `saveSavedTrip`'s own 40-character clamp, so what is
 * typed is what is stored.
 */
export const NameTripDialog = ({
  open,
  onOpenChange,
  title,
  defaultValue = "",
  confirmLabel = "Save",
  onConfirm,
}: Props) => {
  const [name, setName] = useState(defaultValue);

  // Re-seed each time it opens: the same dialog instance is reused for
  // different trips.
  useEffect(() => {
    if (open) setName(defaultValue);
  }, [open, defaultValue]);

  const trimmed = name.trim();

  const submit = () => {
    if (!trimmed) return;
    onConfirm(trimmed);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Something you'll recognise later — "Morning commute", "Mum's house".
          </DialogDescription>
        </DialogHeader>

        <Input
          autoFocus
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Morning commute"
          aria-label="Trip name"
        />

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!trimmed}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
